import {
    useContext,
    useMemo,
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type ITraceTriggerProps from "./type";
import type {
    TraceTriggerWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useTraceCollapsibleContext,
    TraceInteractiveContext,
    TraceContext
} from "../../context";
import type {
    TraceInteractiveContextType
} from "../../type";
import {
    getTraceRowHeight
} from "../../util";
import DisclosureChevron from "../../../collapsible/internal/disclosureChevron";
import TraceHead from "../../internal/head";

const TraceTrigger: FC<ITraceTriggerProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    onStopPress,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    children,
    onFocus,
    onBlur,
    style,
    ...props
}) => {
    const traceContext = useContext(TraceContext);

    const context = useTraceCollapsibleContext({
        part: "Trigger"
    });

    const customLocalize = customLocalizeProp ?? traceContext?.customLocalize;

    const customTheme = customThemeProp ?? traceContext?.customTheme;

    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const rowHeight = getTraceRowHeight({
        isInteractive: true,
        controlHeights,
        touchTargets,
        pointerType
    });

    const {
        container: containerDynamicStyle,
        layer: layerDynamicStyle
    } = useStyles({
        controlHeights,
        isFocusVisible,
        shapeConfigs,
        isHovered,
        isPressed,
        rowHeight,
        radiuses,
        borders,
        colors,
        spaces
    });

    const isActive = isHovered || isPressed;

    const interactive: TraceInteractiveContextType = useMemo(() => ({
        isInteractive: true,
        isActive
    }), [
        isActive
    ]);

    if(!context) {
        return null;
    }

    const webProps: TraceTriggerWebPropsType = Platform.OS === "web" ? {
        "aria-controls": context.contentID
    } : {};

    return <TraceHead
        customLocalize={customLocalize}
        customTheme={customTheme}
        onStopPress={onStopPress}
    >
        <Pressable
            {...props}
            {...webProps}
            {...interactionHandlers}
            aria-expanded={context.isOpen}
            onPress={context.handleToggle}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            id={context.triggerID}
            role="button"
        >
            <View
                style={[
                    stylesheet.layer,
                    layerDynamicStyle
                ]}
                aria-hidden={true}
            />
            <TraceInteractiveContext.Provider
                value={interactive}
            >
                {children}
            </TraceInteractiveContext.Provider>
            <DisclosureChevron
                color={interactive.isActive ? colors.content.icon.high : colors.content.icon.mid}
                customTheme={customTheme}
                isOpen={context.isOpen}
                size={iconSizes.small}
            />
        </Pressable>
    </TraceHead>;
};
export default TraceTrigger;
